import { VehicleDrivetrain, VehicleValuationStatus } from "@/api-schema-enums";
import { Can, useAbility } from "@/casl-provider";
import {
  resolveVehicleValuationErrorDescription,
  VEHICLE_VALUATION_CUSTOM_ERRORS,
} from "@/common/api/vehicle-valuation/_helpers/vehicle-valuation-error-description";
import { useVehicleValuationSelectVehicleMutation } from "@/common/api/vehicle-valuation/use-vehicle-valuation-select-vehicle.mutation";
import { useVehicleValuationVehicleSelectionQuery } from "@/common/api/vehicle-valuation/use-vehicle-valuation-vehicle-selection.query";
import { canVehicleValuationSelectVehicle } from "@/common/helpers/can-vehicle-valuation-select-vehicle";
import { createCurrencyFormatter } from "@/common/helpers/format-currency";
import { useNumberFormatLocale } from "@/common/helpers/number-format";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { cn } from "@/common/helpers/utils";
import { VEHICLE_KIND_LABELS } from "@/common/helpers/vehicle-type-labels";
import { BaseConfirmDialog } from "@/components/confirm-dialog/base-confirm-dialog";
import { TableBackground, tableBackgroundStyles } from "@/components/data-table/types";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckIcon, SearchIcon } from "lucide-react";
import { type KeyboardEvent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";
import {
  getMatchingVehicleValuationSelectionCandidates,
  getSelectedVehicleValuationCandidate,
  getVehicleValuationSelectionColumns,
  getVehicleValuationSelectionMatrix,
  hasDistinguishingVehicleValuationSelectionValue,
  normalizeVehicleValuationSelectionText,
  toggleVehicleValuationSelectionValue,
  type VehicleValuationSelectionCandidate,
  type VehicleValuationSelectionColumn,
  type VehicleValuationSelectionField,
  type VehicleValuationSelectionOption,
  type VehicleValuationSelectionState,
  type VehicleValuationSelectionValues,
} from "./_helpers/vehicle-valuation-selection-columns";

interface VehicleValuationSelectDatVehicleParams {
  uuid: string;
}

interface VehicleSelectionNumberFormatters {
  currency: Intl.NumberFormat;
  decimal: Intl.NumberFormat;
}

function formatDrivetrain(value: unknown, t: (key: string) => string): string {
  switch (value) {
    case VehicleDrivetrain.FrontWheelDrive:
      return t("masterdata:VehicleDrivetrainFront");
    case VehicleDrivetrain.RearWheelDrive:
      return t("masterdata:VehicleDrivetrainRear");
    case VehicleDrivetrain.FourWheelDrive:
      return t("masterdata:VehicleDrivetrainFourWheelDrive");
    case VehicleDrivetrain.UNKNOWN:
      return t("masterdata:VehicleDrivetrainUnknown");
    default:
      return "—";
  }
}

function formatSelectionValue(
  candidate: VehicleValuationSelectionCandidate,
  column: VehicleValuationSelectionColumn,
  language: string,
  t: (key: string) => string,
  numberFormatters: VehicleSelectionNumberFormatters,
): string {
  const value = candidate[column.field];

  if (value === null || value === undefined || value === "") {
    return "—";
  }

  switch (column.format) {
    case "currency":
      return numberFormatters.currency.format(Number(value));
    case "displacement":
      return `${numberFormatters.decimal.format(Number(value))} cm³`;
    case "drivetrain":
      return formatDrivetrain(value, t);
    case "powerKw": {
      const powerPs = candidate.enginePowerPs;
      return powerPs == null ? `${value} kW` : `${value} kW (${powerPs} PS)`;
    }
    case "reimport":
      return value ? t("common:Yes") : t("common:No");
    case "vehicleKind":
      return VEHICLE_KIND_LABELS[String(value)]?.[language === "de" ? "de" : "en"] ?? String(value);
    default:
      return typeof value === "number" ? numberFormatters.decimal.format(value) : String(value);
  }
}

interface VehicleSelectionMatrixOptionProps {
  selectionState: VehicleValuationSelectionState;
  disabled: boolean;
  label: string;
  onSelect: () => void;
}

interface VehicleSelectionSearchResult {
  column: VehicleValuationSelectionColumn;
  label: string;
  value: string;
  selectionState: VehicleValuationSelectionState;
}

function VehicleSelectionMatrixOption({
  selectionState,
  disabled,
  label,
  onSelect,
}: VehicleSelectionMatrixOptionProps) {
  const checked = selectionState !== "unchecked";

  return (
    <button
      type="button"
      aria-pressed={checked}
      disabled={disabled || selectionState === "inferred"}
      data-state={checked ? "selected" : undefined}
      className={cn(
        "border-border bg-background relative flex min-h-10 w-full min-w-0 cursor-pointer items-center gap-2.5 border-b px-4 py-2 text-left text-sm leading-5 break-words transition-colors last:border-b-0 focus-visible:z-10 disabled:pointer-events-none",
        selectionState === "explicit"
          ? "border-primary/20 bg-primary/5 hover:bg-primary/10 font-medium"
          : selectionState === "inferred"
            ? "bg-muted/20"
            : "hover:bg-muted/30",
        disabled && "opacity-50",
      )}
      onClick={onSelect}
    >
      <span
        aria-hidden="true"
        className={cn(
          "border-muted-foreground/60 flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors",
          selectionState === "explicit" && "border-primary bg-primary text-primary-foreground",
          selectionState === "inferred" && "text-muted-foreground",
        )}
      >
        {checked ? <CheckIcon className="size-3" /> : null}
      </span>
      <span className="min-w-0">{label}</span>
    </button>
  );
}

export function VehicleValuationSelectDatVehicleView() {
  return (
    <Can I="read" a="VehicleValuation">
      <VehicleValuationSelectDatVehicleContent />
    </Can>
  );
}

function VehicleValuationSelectDatVehicleContent() {
  const { uuid } = useParams<VehicleValuationSelectDatVehicleParams>();
  const selectionQuery = useVehicleValuationVehicleSelectionQuery(uuid);
  return (
    <VehicleValuationSelectDatVehicleMatrix
      key={`${uuid}:${selectionQuery.data?.snapshotUpdatedAt}`}
      uuid={uuid}
      selectionQuery={selectionQuery}
    />
  );
}

function VehicleValuationSelectDatVehicleMatrix({
  uuid,
  selectionQuery,
}: {
  uuid: string;
  selectionQuery: ReturnType<typeof useVehicleValuationVehicleSelectionQuery>;
}) {
  const { t, i18n } = useTranslation(["common", "fab", "masterdata"]);
  const numberFormatLocale = useNumberFormatLocale();
  const [_, navigate] = useLocation();
  const ability = useAbility();
  const selectionMutation = useVehicleValuationSelectVehicleMutation();
  const [explicitSelectedValues, setExplicitSelectedValues] = useState<VehicleValuationSelectionValues>({});
  const [explicitCandidateIndex, setExplicitCandidateIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmRepickOpen, setConfirmRepickOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const numberFormatters = useMemo<VehicleSelectionNumberFormatters>(
    () => ({
      currency: createCurrencyFormatter({ locale: numberFormatLocale }),
      decimal: new Intl.NumberFormat(numberFormatLocale),
    }),
    [numberFormatLocale],
  );
  const candidates = useMemo(() => selectionQuery.data?.candidates ?? [], [selectionQuery.data?.candidates]);
  const columns = useMemo(() => getVehicleValuationSelectionColumns(candidates), [candidates]);
  const matrix = useMemo(
    () => getVehicleValuationSelectionMatrix(candidates, columns, explicitSelectedValues),
    [candidates, columns, explicitSelectedValues],
  );
  const matchingCandidates = useMemo(
    () => getMatchingVehicleValuationSelectionCandidates(candidates, columns, explicitSelectedValues),
    [candidates, columns, explicitSelectedValues],
  );
  const showCandidateChoiceColumn =
    candidates.length > 0 &&
    (columns.length === 0 ||
      (matchingCandidates.length > 1 && !hasDistinguishingVehicleValuationSelectionValue(matchingCandidates, columns)));
  const selectedCandidate = useMemo(
    () => getSelectedVehicleValuationCandidate(matchingCandidates, explicitCandidateIndex, columns),
    [columns, explicitCandidateIndex, matchingCandidates],
  );
  const commonManufacturer = useMemo(() => {
    const manufacturer = candidates[0]?.manufacturer?.trim();
    if (!manufacturer) {
      return null;
    }

    return candidates.every((candidate) => candidate.manufacturer?.trim() === manufacturer) ? manufacturer : null;
  }, [candidates]);
  const normalizedSearchQuery = normalizeVehicleValuationSelectionText(searchQuery);
  const searchResults = useMemo<VehicleSelectionSearchResult[]>(() => {
    if (!normalizedSearchQuery) {
      return [];
    }

    return matrix.flatMap(({ column, options }) =>
      options.flatMap((option) => {
        const label = formatSelectionValue(option.candidate, column, i18n.language, t, numberFormatters);
        const searchableText = normalizeVehicleValuationSelectionText(`${t(column.translationKey)} ${label}`);

        return searchableText.includes(normalizedSearchQuery)
          ? [{ column, label, value: option.value, selectionState: option.selectionState }]
          : [];
      }),
    );
  }, [i18n.language, matrix, normalizedSearchQuery, numberFormatters, t]);
  const isInitialSelection = selectionQuery.data?.status === VehicleValuationStatus.VehicleSelectionRequired;
  const canSelectVehicle = canVehicleValuationSelectVehicle(selectionQuery.data?.status, {
    canCreate: ability.can("create", "VehicleValuation"),
    canManage: ability.can("manage", "VehicleValuation"),
  });

  const handleValueSelection = (
    field: VehicleValuationSelectionField,
    option: Pick<VehicleValuationSelectionOption, "value" | "selectionState">,
  ) => {
    setExplicitSelectedValues((currentValues) => toggleVehicleValuationSelectionValue(currentValues, field, option));
    setExplicitCandidateIndex(null);
  };

  const handleSearchResultSelection = (result: VehicleSelectionSearchResult) => {
    handleValueSelection(result.column.field, result);
    setSearchQuery("");
    searchInputRef.current?.focus();
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setSearchQuery("");
      return;
    }

    if (event.key !== "Enter" || event.nativeEvent.isComposing || event.keyCode === 229 || searchResults.length !== 1) {
      return;
    }

    if (searchResults[0].selectionState === "inferred") {
      return;
    }

    event.preventDefault();
    handleSearchResultSelection(searchResults[0]);
  };

  const handleSubmit = async () => {
    if (!canSelectVehicle || !selectedCandidate || !selectionQuery.data) {
      return;
    }

    setConfirmRepickOpen(false);
    const snapshotUpdatedAt = selectionQuery.data.snapshotUpdatedAt;
    await toastMutationStatus({
      mutationFn: () =>
        selectionMutation.mutateAsync({
          uuid,
          body: {
            candidateIndex: selectedCandidate.candidateIndex,
            snapshotUpdatedAt,
          },
        }),
      successMessage: t(
        isInitialSelection ? "fab:VehicleSelectionValuationContinued" : "fab:VehicleSelectionRecalculated",
      ),
      errorMessage: t(
        isInitialSelection ? "fab:VehicleSelectionContinueError" : "fab:VehicleSelectionRecalculateError",
      ),
      customErrors: { ...VEHICLE_VALUATION_CUSTOM_ERRORS, 409: t("fab:VehicleSelectionChanged") },
      resolveErrorMessage: resolveVehicleValuationErrorDescription,
      onSuccess: () => navigate(`~/fab/list/${uuid}`),
    });
  };

  if (selectionQuery.isPending) {
    return <Loader size={LoaderSize.Medium} fullscreen isChild />;
  }

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Table} title={t("fab:VehicleSelectionTitle")}>
      {selectionQuery.isError ? (
        <Alert variant="error">
          <AlertTitle>{t("fab:VehicleSelectionLoadError")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {resolveVehicleValuationErrorDescription(selectionQuery.error) ??
                t("fab:VehicleSelectionLoadErrorDescription")}
            </span>
            <Button
              type="button"
              variant={ButtonVariant.Outlined}
              colorVariant={ButtonColorVariant.Secondary}
              onClick={() => void selectionQuery.refetch()}
            >
              {t("fab:RetryDatSnapshotLoad")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {commonManufacturer ? <p className="text-sm font-semibold">{commonManufacturer}</p> : null}

            <div className="relative w-full sm:ml-auto sm:max-w-xl">
              <Input
                ref={searchInputRef}
                type="text"
                role="searchbox"
                autoComplete="off"
                prefix={<SearchIcon className="size-4" />}
                value={searchQuery}
                disabled={selectionMutation.isPending}
                aria-label={t("fab:VehicleSelectionSearchLabel")}
                aria-controls={normalizedSearchQuery ? "vehicle-selection-search-results" : undefined}
                placeholder={t("fab:VehicleSelectionSearchPlaceholder")}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              {normalizedSearchQuery ? (
                <div
                  id="vehicle-selection-search-results"
                  className="border-border bg-popover absolute inset-x-0 top-full z-30 mt-2 max-h-64 overflow-y-auto rounded-md border shadow-md dark:shadow-none"
                >
                  {searchResults.length > 0 ? (
                    <ul aria-label={t("fab:VehicleSelectionSearchResults")}>
                      {searchResults.map((result) => {
                        const selected = result.selectionState === "explicit";
                        const inferred = result.selectionState === "inferred";

                        return (
                          <li key={`${result.column.field}-${result.value}`}>
                            <button
                              type="button"
                              data-selection-search-field={result.column.field}
                              disabled={selectionMutation.isPending || inferred}
                              className={cn(
                                "border-border flex w-full cursor-pointer items-center justify-between gap-3 border-b px-3 py-2 text-left text-sm transition-colors last:border-b-0 disabled:pointer-events-none disabled:opacity-60",
                                selected
                                  ? "bg-primary text-primary-foreground font-semibold"
                                  : inferred
                                    ? "bg-muted/20 text-muted-foreground"
                                    : "hover:bg-accent hover:text-accent-foreground",
                              )}
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => handleSearchResultSelection(result)}
                            >
                              <span className="min-w-0 break-words">{result.label}</span>
                              <span
                                className={cn(
                                  "shrink-0 text-xs",
                                  selected ? "text-primary-foreground" : "text-muted-foreground",
                                )}
                              >
                                {t(result.column.translationKey)}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p role="status" className="text-muted-foreground px-3 py-4 text-center text-sm">
                      {t("fab:VehicleSelectionSearchNoResults")}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div className="border-border relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg border">
            <div className="h-full min-w-0 scrollbar-none overflow-auto rounded-lg sm:scrollbar-thin [&::-webkit-scrollbar]:hidden sm:[&::-webkit-scrollbar]:block">
              <div className="w-fit min-w-full">
                <div aria-hidden="true" className="sticky top-0 z-10 h-0">
                  <div
                    className={cn(
                      "h-9 rounded-t-lg backdrop-blur-sm",
                      tableBackgroundStyles[TableBackground.Background],
                    )}
                  />
                </div>
                <Table className="w-max min-w-full table-fixed">
                  <TableHeader className="sticky top-0 z-10">
                    <TableRow className="border-0 hover:bg-transparent">
                      {matrix.map(({ column }) => (
                        <TableHead
                          key={column.field}
                          className="border-border w-52 max-w-52 min-w-52 border-r whitespace-nowrap last:border-r-0"
                        >
                          {t(column.translationKey)}
                        </TableHead>
                      ))}
                      {showCandidateChoiceColumn ? (
                        <TableHead className="w-52 max-w-52 min-w-52 whitespace-nowrap">
                          {t("fab:VehicleSelectionChoose")}
                        </TableHead>
                      ) : null}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="hover:bg-transparent">
                      {matrix.map(({ column, options }) => (
                        <TableCell
                          key={column.field}
                          className="border-border h-auto w-52 max-w-52 min-w-52 border-r p-0 align-top whitespace-normal group-hover:bg-transparent group-hover:bg-none last:border-r-0"
                        >
                          <div role="group" aria-label={t(column.translationKey)}>
                            {options.map((option) => (
                              <VehicleSelectionMatrixOption
                                key={option.value}
                                selectionState={option.selectionState}
                                disabled={selectionMutation.isPending}
                                label={formatSelectionValue(
                                  option.candidate,
                                  column,
                                  i18n.language,
                                  t,
                                  numberFormatters,
                                )}
                                onSelect={() => handleValueSelection(column.field, option)}
                              />
                            ))}
                          </div>
                        </TableCell>
                      ))}
                      {showCandidateChoiceColumn ? (
                        <TableCell className="h-auto w-52 max-w-52 min-w-52 p-0 align-top whitespace-normal group-hover:bg-transparent group-hover:bg-none">
                          <div role="group" aria-label={t("fab:VehicleSelectionChoose")}>
                            {matchingCandidates.map((candidate) => (
                              <VehicleSelectionMatrixOption
                                key={candidate.candidateIndex}
                                selectionState={
                                  explicitCandidateIndex === candidate.candidateIndex
                                    ? "explicit"
                                    : matchingCandidates.length === 1
                                      ? "inferred"
                                      : "unchecked"
                                }
                                disabled={selectionMutation.isPending}
                                label={t("fab:VehicleSelectionOption", {
                                  number: candidate.candidateIndex + 1,
                                })}
                                onSelect={() =>
                                  setExplicitCandidateIndex((currentIndex) =>
                                    currentIndex === candidate.candidateIndex ? null : candidate.candidateIndex,
                                  )
                                }
                              />
                            ))}
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 pt-4 sm:flex-row sm:items-center sm:justify-end">
            <Button
              type="button"
              variant={ButtonVariant.Outlined}
              colorVariant={ButtonColorVariant.Secondary}
              disabled={selectionMutation.isPending}
              onClick={() => navigate(isInitialSelection ? "~/fab/list" : `~/fab/list/${uuid}`)}
            >
              {t("common:Cancel")}
            </Button>
            {canSelectVehicle ? (
              <Button
                type="button"
                disabled={!selectedCandidate || selectionMutation.isPending}
                onClick={() => (isInitialSelection ? void handleSubmit() : setConfirmRepickOpen(true))}
              >
                {selectionMutation.isPending ? (
                  <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                ) : null}
                {t(isInitialSelection ? "fab:VehicleSelectionContinue" : "fab:VehicleSelectionRecalculate")}
              </Button>
            ) : null}
          </div>
        </div>
      )}
      <BaseConfirmDialog
        open={confirmRepickOpen}
        title={t("fab:VehicleSelectionRecalculate")}
        description={t("fab:VehicleSelectionRecalculateConfirmation")}
        labelCancel={t("common:Cancel")}
        labelConfirm={t("fab:VehicleSelectionRecalculate")}
        onCancel={() => setConfirmRepickOpen(false)}
        onConfirm={() => void handleSubmit()}
      />
    </CenteredLayout>
  );
}
